import { logger } from './logger';

export type CircuitState = 'closed' | 'open' | 'half-open';

/**
 * Custom error thrown when circuit breaker is open
 * Use instanceof to check for circuit breaker errors
 */
export class CircuitBreakerError extends Error {
  public readonly remainingTimeSeconds: number;

  constructor(message: string, remainingTimeSeconds: number) {
    super(message);
    this.name = 'CircuitBreakerError';
    this.remainingTimeSeconds = remainingTimeSeconds;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, CircuitBreakerError);
    }
  }
}

export interface CircuitBreakerOptions {
  failureThreshold: number;      // Número de falhas para abrir o circuito (default: 5)
  resetTimeout: number;           // Tempo em ms para tentar half-open (default: 60000 = 1min)
  name: string;                   // Nome do circuit para logging
}

/**
 * Circuit Breaker pattern implementation
 * Prevents cascading failures by stopping requests to failing services
 *
 * States:
 * - CLOSED: Normal operation, requests pass through
 * - OPEN: Service is failing, requests fail fast without trying
 * - HALF-OPEN: Testing if service recovered, allows one request
 *
 * @example
 * const breaker = new CircuitBreaker({ name: 'ResaleAPI', failureThreshold: 5, resetTimeout: 60000 });
 * const result = await breaker.execute(() => apiCall());
 */
export class CircuitBreaker {
  private state: CircuitState = 'closed';
  private failureCount = 0;
  private lastFailureTime = 0;
  private successCount = 0;

  private readonly failureThreshold: number;
  private readonly resetTimeout: number;
  private readonly name: string;

  constructor(options: CircuitBreakerOptions) {
    this.failureThreshold = options.failureThreshold;
    this.resetTimeout = options.resetTimeout;
    this.name = options.name;
  }

  /**
   * Execute a function with circuit breaker protection
   */
  async execute<T>(fn: () => Promise<T>): Promise<T> {
    // Check if circuit is open
    if (this.state === 'open') {
      const timeSinceLastFailure = Date.now() - this.lastFailureTime;

      // If reset timeout passed, try half-open
      if (timeSinceLastFailure >= this.resetTimeout) {
        logger.info(`[CircuitBreaker:${this.name}] Transitioning to HALF-OPEN state`);
        this.state = 'half-open';
        this.successCount = 0;
      } else {
        // Fail fast - don't try the request
        const remainingTime = Math.ceil((this.resetTimeout - timeSinceLastFailure) / 1000);
        logger.warn(`[CircuitBreaker:${this.name}] Circuit is OPEN, failing fast. Retry in ${remainingTime}s`);
        throw new CircuitBreakerError(
          `Service temporarily unavailable. Retry in ${remainingTime} seconds.`,
          remainingTime
        );
      }
    }

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  private onSuccess(): void {
    this.failureCount = 0;

    if (this.state === 'half-open') {
      this.successCount++;
      // After 2 successful requests in half-open, close the circuit
      if (this.successCount >= 2) {
        logger.info(`[CircuitBreaker:${this.name}] Service recovered, transitioning to CLOSED`);
        this.state = 'closed';
        this.successCount = 0;
      }
    }
  }

  private onFailure(): void {
    this.failureCount++;
    this.lastFailureTime = Date.now();

    logger.warn(`[CircuitBreaker:${this.name}] Failure count: ${this.failureCount}/${this.failureThreshold}`);

    if (this.state === 'half-open') {
      // If half-open test failed, go back to open
      logger.error(`[CircuitBreaker:${this.name}] Half-open test failed, transitioning to OPEN`);
      this.state = 'open';
      this.successCount = 0;
      return;
    }

    // Open circuit if threshold reached
    if (this.failureCount >= this.failureThreshold) {
      logger.error(`[CircuitBreaker:${this.name}] Threshold reached, transitioning to OPEN`);
      this.state = 'open';
    }
  }

  /**
   * Get current circuit state
   */
  getState(): CircuitState {
    return this.state;
  }

  /**
   * Manually reset the circuit breaker
   */
  reset(): void {
    this.state = 'closed';
    this.failureCount = 0;
    this.successCount = 0;
    this.lastFailureTime = 0;
    logger.info(`[CircuitBreaker:${this.name}] Manually reset to CLOSED`);
  }
}
